import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Row, Col, Input, Select, DatePicker } from 'antd';
import { Payable, PayableStatus, PayableType } from '@/types/finance';
import { VendorSelector } from '@/components/common/VendorSelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { AssetSelector } from '@/components/common/AssetSelector';
import CurrencyInput from '@/components/common/CurrencyInput';
import dayjs, { Dayjs } from 'dayjs';
import { Money } from '@/types';
import { formatFirestoreTimestamp } from '@/utils/date';

interface PayableModalProps {
  open: boolean;
  payable?: Payable | null;
  onSuccess: (data: Omit<Payable, 'id' | 'activityRecords' | 'workspace'>) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface PayableFormValues {
  name: string;
  description: string;
  type: PayableType;
  status: PayableStatus;
  amount: Money;
  dueDate: Dayjs;
  notes?: string;
  vendorId?: string;
  employeeId?: string;
  assetId?: string;
}

const PayableModal: React.FC<PayableModalProps> = ({
  open,
  payable,
  onSuccess,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation(['payables', 'common']);
  const [form] = Form.useForm<PayableFormValues>();
  const [selectedType, setSelectedType] = React.useState<PayableType | undefined>(payable?.type);
  const [vendorData, setVendorData] = React.useState<{ id: string; name: string } | null>(payable?.vendor ? { id: payable.vendor.id, name: payable.vendor.name } : null);
  const [employeeData, setEmployeeData] = React.useState<{ id: string; name: string } | null>(payable?.employee ? { id: payable.employee.id, name: payable.employee.name } : null);
  const [assetData, setAssetData] = React.useState<{ id: string; name: string } | null>(payable?.asset ? { id: payable.asset.id, name: payable.asset.name } : null);

  const payableTypes = [
    { text: t('payables:types.vendor'), value: 'vendor' },
    { text: t('payables:types.asset'), value: 'asset' },
    { text: t('payables:types.employee'), value: 'employee' },
    { text: t('payables:types.payroll'), value: 'payroll' },
    { text: t('payables:types.maintenance'), value: 'maintenance' },
    { text: t('payables:types.other'), value: 'other' },
  ];

  const payableStatuses = [
    { text: t('payables:statuses.pending'), value: 'pending' },
    { text: t('payables:statuses.partially_paid'), value: 'partial' },
    { text: t('payables:statuses.paid'), value: 'paid' },
    { text: t('payables:statuses.overdue'), value: 'overdue' },
    { text: t('payables:statuses.cancelled'), value: 'cancelled' },
  ];

  const handleTypeChange = (type: PayableType) => {
    setSelectedType(type);
    // Clear vendor, employee, and asset data when type changes
    if (type !== 'vendor') {
      form.setFieldValue('vendorId', undefined);
      setVendorData(null);
    }
    if (type !== 'employee') {
      form.setFieldValue('employeeId', undefined);
      setEmployeeData(null);
    }
    if (type !== 'asset') {
      form.setFieldValue('assetId', undefined);
      setAssetData(null);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payableData = {
        ...values,
        amount: values.amount, // Keep the full Money object with currency and amount
        dueDate: values.dueDate?.toDate(),
        vendor: selectedType === 'vendor' && vendorData ? { id: vendorData.id, name: vendorData.name } : undefined,
        employee: selectedType === 'employee' && employeeData ? { id: employeeData.id, name: employeeData.name } : undefined,
        asset: selectedType === 'asset' && assetData ? { id: assetData.id, name: assetData.name } : undefined,
      };
      onSuccess(payableData);
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedType(undefined);
    setVendorData(null);
    setEmployeeData(null);
    setAssetData(null);
    onCancel();
  };

  React.useEffect(() => {
    if (open && payable) {
      // Handle Firestore timestamp conversion for dueDate using the utility function
      const dueDate = payable.dueDate 
        ? dayjs(formatFirestoreTimestamp(payable.dueDate))
        : undefined;

      form.setFieldsValue({
        ...payable,
        dueDate,
        vendorId: payable.vendor?.id,
        employeeId: payable.employee?.id,
        assetId: payable.asset?.id,
      });
      setSelectedType(payable.type);
      setVendorData(payable.vendor ? { id: payable.vendor.id, name: payable.vendor.name } : null);
      setEmployeeData(payable.employee ? { id: payable.employee.id, name: payable.employee.name } : null);
      setAssetData(payable.asset ? { id: payable.asset.id, name: payable.asset.name } : null);
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        status: 'pending',
        type: 'other',
      });
      setSelectedType(undefined);
      setVendorData(null);
      setEmployeeData(null);
      setAssetData(null);
    }
  }, [open, payable, form]);

  return (
    <Modal
      title={
        payable
          ? t('payables:form.updateTitle')
          : t('payables:form.createTitle')
      }
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'pending',
          type: 'other',
        }}
      >
        {/* Name Row */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label={t('common:name')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Input placeholder={t('common:name')} />
            </Form.Item>
          </Col>
        </Row>

        {/* Description Row */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={t('common:description')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Input.TextArea
                placeholder={t('common:description')}
                rows={3}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Type and Associated Selector Row */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('common:typeLabel')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Select
                placeholder={t('common:select')}
                options={payableTypes}
                onChange={handleTypeChange}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* Conditional Vendor Selector */}
            {selectedType === 'vendor' && (
              <VendorSelector
                onVendorDataChange={setVendorData}
              />
            )}

            {/* Conditional Asset Selector */}
            {selectedType === 'asset' && (
              <AssetSelector
                onAssetDataChange={setAssetData}
              />
            )}

            {/* Conditional Employee Selector */}
            {selectedType === 'employee' && (
              <EmployeeSelector
                onEmployeeDataChange={setEmployeeData}
              />
            )}
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="amount"
              label={t('common:amount')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
                {
                  validator: (_, value) => {
                    if (!value || !value.amount || value.amount <= 0) {
                      return Promise.reject(new Error(t('common:invalid')));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <CurrencyInput />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dueDate"
              label={t('common:dueDate')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <DatePicker
                placeholder={t('common:dueDate')}
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('common:statusLabel')}
              rules={[
                {
                  required: true,
                  message: t('common:fieldRequired'),
                },
              ]}
            >
              <Select
                placeholder={t('common:select')}
                options={payableStatuses}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label={t('common:notes')}
        >
          <Input.TextArea
            placeholder={t('common:notes')}
            rows={2}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PayableModal; 